import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Star, ShoppingCart, Filter, Truck } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import img1 from '../Components/Images/industry.jpg'

export default function IndustrialScalesPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [showFilter, setShowFilter] = useState(false);

  // Product data
  const scales = [
    {
      id: 1,
      name: "Heavy Duty Platform Scale",
      category: "platform",
      capacity: "5000 kg",
      accuracy: "±0.1%",
      platform: "120 x 120 cm",
      price: 2499.99,
      rating: 4.8,
      reviews: 124,
      image: "/api/placeholder/600/400",
      featured: true,
      description: "High precision industrial platform scale with heavy-duty construction, perfect for warehouse and manufacturing environments."
    },
    {
      id: 2,
      name: "Digital Bench Scale",
      category: "bench",
      capacity: "150 kg",
      accuracy: "±0.05%",
      platform: "45 x 60 cm",
      price: 899.99,
      rating: 4.6,
      reviews: 89,
      image: "/api/placeholder/600/400",
      featured: false,
      description: "Versatile bench scale with high precision and durable stainless steel construction for production and quality control applications."
    },
    {
      id: 3,
      name: "Floor Scale with Ramps",
      category: "floor",
      capacity: "2000 kg",
      accuracy: "±0.1%",
      platform: "150 x 150 cm",
      price: 3299.99,
      rating: 4.7,
      reviews: 72,
      image: "/api/placeholder/600/400",
      featured: true,
      description: "Low-profile floor scale with built-in ramps for easy pallet jack and cart access, ideal for shipping and receiving areas."
    },
    {
      id: 4,
      name: "Precision Balance Scale",
      category: "precision",
      capacity: "30 kg",
      accuracy: "±0.01%",
      platform: "40 x 40 cm",
      price: 1299.99,
      rating: 4.9,
      reviews: 58,
      image: "/api/placeholder/600/400",
      featured: false,
      description: "High-accuracy balance scale with advanced calibration features for laboratory and quality control applications."
    },
    {
      id: 5,
      name: "Crane Scale",
      category: "crane",
      capacity: "10000 kg",
      accuracy: "±0.2%",
      platform: "N/A",
      price: 4599.99,
      rating: 4.5,
      reviews: 41,
      image: "/api/placeholder/600/400",
      featured: true,
      description: "Heavy-duty crane scale with wireless remote display and long battery life for construction and industrial lifting operations."
    },
    {
      id: 6,
      name: "Pallet Jack Scale",
      category: "pallet",
      capacity: "2500 kg",
      accuracy: "±0.1%",
      platform: "Fork dimensions: 160 x 55 cm",
      price: 3899.99,
      rating: 4.6,
      reviews: 63,
      image: "/api/placeholder/600/400",
      featured: false,
      description: "Integrated weighing pallet jack for efficient material handling and weight verification in one operation."
    }
  ];

  // Filter products based on active tab
  const filteredScales = activeTab === 'all' 
    ? scales 
    : activeTab === 'featured' 
      ? scales.filter(scale => scale.featured) 
      : scales.filter(scale => scale.category === activeTab);

  // Categories for filter tabs
  const categories = [
    { id: 'all', name: 'All Scales' },
    { id: 'featured', name: 'Featured' },
    { id: 'platform', name: 'Platform' },
    { id: 'floor', name: 'Floor' },
    { id: 'bench', name: 'Bench' },
    { id: 'crane', name: 'Crane' },
    { id: 'precision', name: 'Precision' }
  ];

  // Format price with commas
  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
  };

  // Generate star rating display
  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={`${i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'} fill-current`}
          />
        ))}
        <span className="ml-1 text-sm font-semibold">{rating}</span>
        {/* <span className="ml-1 text-xs text-gray-500">({reviews} reviews)</span> */}
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4 py-20">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
                Industrial Weighing Solutions
              </h1>
              <p className="text-xl mb-8 text-blue-100">
                Precision engineered scales for manufacturing, warehousing, and logistics operations
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="bg-white text-blue-900 px-6 py-3 rounded-md font-semibold hover:bg-blue-50 transition flex items-center">
                  View Products <ArrowRight className="ml-2" size={18} />
                </button>
                <button className="border-2 border-white px-6 py-3 rounded-md font-semibold hover:bg-white hover:bg-opacity-10 transition">
                  Request Quote
                </button>
              </div>
            </div>
            <div className="md:w-1/2">
              <img
                src=""
                alt="Industrial Scale"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Scales</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="bg-blue-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
              <CheckCircle className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-bold mb-2">High Precision</h3>
            <p className="text-gray-600">
              Industry-leading accuracy with advanced calibration technology for consistent measurements.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="bg-blue-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
              <Truck className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-bold mb-2">Durable Construction</h3>
            <p className="text-gray-600">
              Built with industrial-grade materials to withstand harsh environments and heavy usage.
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="bg-blue-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
              <CheckCircle className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-bold mb-2">Smart Connectivity</h3>
            <p className="text-gray-600">
              Seamless integration with your systems through advanced data connectivity options.
            </p>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold">Our Industrial Scales</h2>
          <button 
            onClick={() => setShowFilter(!showFilter)}
            className="flex items-center text-blue-600 font-medium"
          >
            <Filter size={18} className="mr-2" />
            Filter Options
          </button>
        </div>

        {/* Category Tabs */}
        {showFilter && (
          <div className="bg-white p-4 rounded-lg shadow-md mb-8 overflow-x-auto">
            <div className="flex space-x-2 min-w-max">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveTab(category.id)}
                  className={`px-4 py-2 rounded-md whitespace-nowrap ${
                    activeTab === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredScales.map(scale => (
            <div key={scale.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
              <div className="relative">
                <img src={scale.image} alt={scale.name} className="w-full h-64 object-cover" />
                {scale.featured && (
                  <div className="absolute top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                    Featured
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{scale.name}</h3>
                <p className="text-gray-600 mb-4">{scale.description}</p>
                
                <div className="mb-4">
                  {renderStars(scale.rating)}
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Capacity</p>
                    <p className="font-semibold">{scale.capacity}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Accuracy</p>
                    <p className="font-semibold">{scale.accuracy}</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-700">{formatPrice(scale.price)}</span>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition">
                    <ShoppingCart size={18} className="mr-2" />
                    Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Call To Action */}
      <div className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Need Custom Weighing Solutions?</h2>
            <p className="text-lg text-gray-300 mb-8">
              Our team of experts will help you find the perfect scale for your specific industrial requirements
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button className="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-md font-semibold transition">
                Contact Sales Team
              </button>
              <button className="border border-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-md font-semibold transition">
                Download Catalog
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      
    </div>
  );
}